import React, { memo } from "react";
import {
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Platform,
} from "react-native";
import Svg, { Circle } from "react-native-svg";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { router } from "expo-router";
import { UserFinanceDataResponse, CategoryBudget } from "@/types/financeData";

interface BudgetsCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Category to emoji mapping (exported for use in other components)
export const CATEGORY_EMOJI: Record<string, string> = {
  FOOD_AND_DRINK: "🍽️",
  GENERAL_MERCHANDISE: "🛍️",
  ENTERTAINMENT: "🎬",
  PERSONAL_CARE: "💅",
  TRAVEL: "✈️",
  GENERAL_SERVICES: "🛠️",
  HOME_IMPROVEMENT: "🏡",
  MEDICAL: "🏥",
  TRANSPORTATION: "🚗",
};

// Category display names (exported for use in other components)
export const CATEGORY_DISPLAY_NAME: Record<string, string> = {
  FOOD_AND_DRINK: "Food & Drink",
  GENERAL_MERCHANDISE: "Merchandise",
  ENTERTAINMENT: "Entertainment",
  PERSONAL_CARE: "Personal Care",
  TRAVEL: "Travel",
  GENERAL_SERVICES: "Services",
  HOME_IMPROVEMENT: "Home",
  MEDICAL: "Medical",
  TRANSPORTATION: "Transport",
};

// Helper to format currency from cents
const formatCurrency = (cents: number): string => {
  const dollars = Math.abs(cents) / 100;
  const isWholeNumber = dollars % 1 === 0;
  return (
    "$" +
    dollars.toLocaleString("en-US", {
      minimumFractionDigits: isWholeNumber ? 0 : 2,
      maximumFractionDigits: isWholeNumber ? 0 : 2,
    })
  );
};

// Circular progress component
const CircularProgress = ({
  progress,
  isOver,
  emoji,
}: {
  progress: number;
  isOver: boolean;
  emoji: string;
}) => {
  const size = 48;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(progress, 1);
  const strokeDashoffset = circumference * (1 - clampedProgress);

  // Colors
  const progressColor = isOver ? "#EF4444" : "#0F9129"; // Red if over, green if under
  const bgColor = "#E5E7EB";

  return (
    <View style={styles.progressContainer}>
      <Svg width={size} height={size} style={styles.progressSvg}>
        {/* Background circle */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={bgColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress circle */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      {/* Emoji in center */}
      <View style={styles.emojiContainer}>
        <Text style={styles.emoji}>{emoji}</Text>
      </View>
    </View>
  );
};

// Single budget item
const BudgetItem = ({ budget }: { budget: CategoryBudget }) => {
  const { category, budgetAmount, spentAmount } = budget;
  const emoji = CATEGORY_EMOJI[category] || "💰";

  // Calculate progress and remaining
  const progress = budgetAmount > 0 ? spentAmount / budgetAmount : 0;
  const isOver = spentAmount > budgetAmount;
  const difference = isOver
    ? spentAmount - budgetAmount
    : budgetAmount - spentAmount;

  return (
    <View style={styles.budgetItem}>
      <CircularProgress progress={progress} isOver={isOver} emoji={emoji} />
      <Text style={styles.amount}>{formatCurrency(difference)}</Text>
      <Text style={[styles.status, isOver && styles.statusOver]}>
        {isOver ? "over" : "left"}
      </Text>
    </View>
  );
};

export default memo(function BudgetsCard({ financeData }: BudgetsCardProps) {
  const budgets = financeData?.analysis?.budgets;

  // Return null if no budgets
  if (!budgets?.length) {
    return null;
  }

  const handleViewAll = () => {
    router.push("/(tabs)/my-money/budget");
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Budgets</Text>
        <TouchableOpacity onPress={handleViewAll} style={styles.viewAllButton}>
          <Text style={styles.viewAllText}>View all</Text>
        </TouchableOpacity>
      </View>

      {/* Horizontal scroll of budget items */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {budgets.map((budget, index) => (
          <BudgetItem key={budget.category || index} budget={budget} />
        ))}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 16,
    paddingBottom: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  viewAllButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#0F9129",
  },
  viewAllText: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#0F9129",
  },
  scrollContent: {
    paddingRight: 16,
    gap: 20,
  },
  budgetItem: {
    alignItems: "center",
    width: 60,
  },
  progressContainer: {
    width: 48,
    height: 48,
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },
  progressSvg: {
    position: "absolute",
  },
  emojiContainer: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
  },
  emoji: {
    fontSize: Platform.OS === "ios" ? 20 : 18,
  },
  amount: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    marginTop: 8,
  },
  status: {
    fontSize: 11,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    marginTop: 2,
  },
  statusOver: {
    color: "#EF4444",
  },
});
